import { createEffect, createEvent, createStore } from "effector";
import { Package, PackageAvailability } from "../models";
import { PackagesEndpoint } from "../endpoints";
import { confirmChangePackages } from "./confirmChangeFns";
import { resetAllSelectedStores } from "./resetAllSelected";

const resetSelectedPackages = createEvent("reset selected person types");
export type SelectedPackage = {
  entity: Package;
  personTypes: {
    id: number;
    name: string;
    quantity: number;
  }[];
  guestsQuantity: number;
  capacityPackageInstancesQuantity: number;
  activitiesOrder: { name: string; id: number }[];
  availabilitySlot: PackageAvailability;
  availabilitySlots: PackageAvailability[];
  instancesIds: string[];
  supplementsServing: {
    id: number;
    name: string;
    description: string;
    avatar: string | null;
    quantity: number;
    servingActivities: { id: number; name: string }[];
    servingActivityId: number | null;
  }[];
};
export const $selectedPackages = createStore<SelectedPackage[]>([])
  .reset(resetSelectedPackages)
  .reset(resetAllSelectedStores);

// ASYNC EFFECTS
export const selectedPackagesApi = {
  save: createEffect(
    async (
      value: Omit<
        SelectedPackage,
        | "guestsQuantity"
        | "supplementsServing"
        | "capacityPackageInstancesQuantity"
      >
    ): Promise<SelectedPackage> => {
      if (!(await confirmChangePackages())) {
        throw new Error("User declined package change.");
      }

      const guestsQuantity = value.personTypes.reduce(
        (acc, { quantity }) => acc + quantity,
        0
      );

      const capacityPackageInstancesQuantity = value.entity.capacity
        ? Math.ceil(guestsQuantity / value.entity.capacity)
        : 0;

      const supplementsServing = value.entity.supplements
        .filter(({ visible }) => visible)
        .map((supplement) => {
          return {
            id: supplement.id,
            name: supplement.name,
            description: supplement.description,
            avatar: supplement.avatar,
            quantity:
              supplement.quantity *
              (capacityPackageInstancesQuantity || guestsQuantity),
            servingActivities: value.entity.activities
              .map(({ id, name }) => ({
                id,
                name,
              }))
              .filter(({ id }) => {
                // filter according to package.supplement[0].servingActivities
                if (
                  supplement.servingActivities &&
                  supplement.servingActivities.length > 0
                ) {
                  return supplement.servingActivities.includes(id);
                }
                return false;
              }),
            servingActivityId: null,
          };
        });

      return {
        ...value,
        guestsQuantity,
        supplementsServing,
        capacityPackageInstancesQuantity,
      };
    }
  ),
  remove: createEffect(
    async (value: { id: number }): Promise<{ id: number }> => {
      if (!(await confirmChangePackages())) {
        throw new Error("User declined package change.");
      }

      const store = $selectedPackages.getState();
      const idx = store.findIndex(({ entity: { id } }) => id === value.id);

      if (~idx) {
        const { instancesIds } = store[idx];
        await PackagesEndpoint.cancelReservation.call(instancesIds);
      }

      return value;
    }
  ),
  cancelAllPackagesReserve: createEffect(async (): Promise<boolean | Error> => {
    const activities = $selectedPackages.getState();

    const instancesIds = activities.reduce<string[]>(
      (acc, { instancesIds }) => [...acc, ...instancesIds],
      []
    );

    if (instancesIds.length === 0) return true;

    try {
      await PackagesEndpoint.cancelReservation.call(instancesIds);
      return true;
    } catch (e) {
      throw new Error(
        `Error while cancelling instances reserving: ${instancesIds}`
      );
    }
  }),
  setSupplementServingActivity: createEvent<{
    packageId: number;
    supplementId: number;
    activityId: number | null;
  }>(),
  reset: async (): Promise<boolean> => {
    try {
      await selectedPackagesApi.cancelAllPackagesReserve();
      resetSelectedPackages();
      return true;
    } catch (e) {
      return false;
    }
  },
};

// STORES CHANGES
$selectedPackages.on(selectedPackagesApi.save.doneData, (store, value) => {
  const storeCloned = [...store];

  const idx = storeCloned.findIndex(
    ({ entity: { id } }) => id === value.entity.id
  );
  if (~idx) {
    storeCloned[idx] = value;
  } else {
    storeCloned.push(value);
  }

  return storeCloned;
});

$selectedPackages.on(selectedPackagesApi.remove.doneData, (store, value) => {
  const storeCloned = [...store];

  const idx = storeCloned.findIndex(({ entity: { id } }) => id === value.id);
  if (~idx) storeCloned.splice(idx, 1);

  return storeCloned;
});

$selectedPackages.on(
  selectedPackagesApi.setSupplementServingActivity,
  (store, value) => {
    const storeCloned = [...store];

    const { packageId, supplementId, activityId } = value;

    const package_ = storeCloned.find(({ entity: { id } }) => id === packageId);
    if (package_) {
      const supplement = package_.supplementsServing.find(
        ({ id }) => id === supplementId
      );
      if (supplement) {
        supplement.servingActivityId = activityId;
      }
    }

    return storeCloned;
  }
);

$selectedPackages.on(
  selectedPackagesApi.cancelAllPackagesReserve.doneData,
  (store) => {
    return [...store].map((item) => {
      return {
        ...item,
        instancesIds: [],
      };
    });
  }
);
